import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/payments')
const GET_PAYMENTS_ENDPOINT = `${API_BASE_URL}/all`
const GET_TOTAL_PAYMENTS_ENDPOINT = `${API_BASE_URL}/totalPayments`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Payment request failed (${response.status}) at ${url}`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findPayments(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['payments', 'paymentList', 'results', 'items', 'data']) {
    const payments = findPayments(data[key])
    if (payments.length || Array.isArray(data[key])) return payments
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.payments) ??
    findNextCursor(data.paymentList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

const addParam = (params, key, value) => {
  if (value === undefined || value === null || value === '') return
  params.set(key, String(value))
}

export async function getPayments({
  cursor = null,
  page = 1,
  fromDate = '',
  toDate = '',
  fromAmount = '',
  toAmount = '',
  customer = '',
  customerType = '',
  orderType = '',
  salesExecutive = '',
  paymentMethod = '',
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  addParam(params, 'fromDate', fromDate)
  addParam(params, 'toDate', toDate)
  addParam(params, 'fromAmount', fromAmount)
  addParam(params, 'toAmount', toAmount)
  addParam(params, 'customer', customer)
  addParam(params, 'customerType', customerType)
  addParam(params, 'orderType', orderType)
  addParam(params, 'salesExecutive', salesExecutive)
  addParam(params, 'paymentMethod', paymentMethod)

  const data = await request(`${GET_PAYMENTS_ENDPOINT}?${params}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const payments = findPayments(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const totalPayments =
    payload?.totalPayments ??
    payload?.total_payments ??
    data?.totalPayments ??
    data?.total_payments ??
    0
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    payments,
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
    totalPayments,
  }
}

export async function getTotalPayments() {
  const data = await request(GET_TOTAL_PAYMENTS_ENDPOINT)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data

  if (typeof data === 'number' || typeof data === 'string') {
    return Number(data) || 0
  }

  return Number(
    payload?.totalPayments ??
      payload?.total_payments ??
      payload?.total ??
      data?.totalPayments ??
      data?.total_payments ??
      data?.total ??
      0,
  )
}
